export type LiveCaptureSource = HTMLVideoElement | HTMLCanvasElement;

export function visibleVideoFrame(video: LiveCaptureSource) {
  const sourceWidth = 'videoWidth' in video ? video.videoWidth : video.width;
  const sourceHeight = 'videoHeight' in video ? video.videoHeight : video.height;
  const rect = video.getBoundingClientRect();
  const displayWidth = rect.width || sourceWidth;
  const displayHeight = rect.height || sourceHeight;
  const displayRatio = displayWidth / displayHeight;
  const sourceRatio = sourceWidth / sourceHeight;
  const width = sourceRatio > displayRatio ? sourceHeight * displayRatio : sourceWidth;
  const height = sourceRatio < displayRatio ? sourceWidth / displayRatio : sourceHeight;

  return {
    x: (sourceWidth - width) / 2,
    y: (sourceHeight - height) / 2,
    width,
    height,
    displayRatio,
  };
}

export async function captureLiveImage(video: LiveCaptureSource | null): Promise<File> {
  if (!video || ('videoWidth' in video
    ? video.readyState < 2 || !video.videoWidth || !video.videoHeight || video.ended
    : !video.width || !video.height)) {
    throw new Error('캡처할 방송 영상이 없습니다. 라이브 화면이 재생된 뒤 다시 시도해주세요.');
  }

  const canvas = document.createElement('canvas');
  const frame = visibleVideoFrame(video);
  const scale = Math.min(1, 1920 / Math.max(frame.width, frame.height));
  canvas.width = Math.max(1, Math.round(frame.width * scale));
  canvas.height = Math.max(1, Math.round(frame.height * scale));
  const context = canvas.getContext('2d');
  if (!context) throw new Error('이 브라우저에서는 영상 캡처를 지원하지 않습니다.');

  try {
    context.drawImage(video, frame.x, frame.y, frame.width, frame.height, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(result => result?.size
        ? resolve(result)
        : reject(new Error('캡처 이미지를 만들지 못했습니다. 다시 시도해주세요.')), 'image/jpeg', 0.9);
    });
    if (blob.size > 10 * 1024 * 1024) throw new Error('캡처 사진이 10MB를 초과했습니다. 사진 파일을 선택해주세요.');
    return new File([blob], `live-capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
  } catch (error) {
    if (error instanceof Error && error.name === 'SecurityError') {
      throw new Error('방송 영상의 보안 설정으로 캡처할 수 없습니다. 상품 사진을 파일로 추가해주세요.');
    }
    throw error;
  }
}
